import React, { useMemo } from 'react';
import { renderMarkdown } from './markdown';
import ToolCallTrace from './ToolCallTrace';
import ReportMessage from './ReportMessage';

// Build a flat list of {id, name, hovercard} entries from a message's
// own tool_results so the markdown renderer can chip the brand
// mentions inline. Looks for the common payload shapes the tools
// produce (`rows: [{id, name}]`, `row: {id, name}`).
function brandIndexFromMessage(message) {
  const entries = [];
  const seen = new Set();
  const pushBrand = (b) => {
    if (!b || !b.id || !b.name) return;
    const key = `${b.id}:${b.name}`;
    if (seen.has(key)) return;
    seen.add(key);
    const hovercard = [
      b.category,
      [b.hq_city, b.hq_state || b.country].filter(Boolean).join(', '),
    ].filter(Boolean).join(' · ');
    entries.push({ id: b.id, name: b.name, hovercard });
  };
  const walk = (result) => {
    if (!result || typeof result !== 'object') return;
    if (Array.isArray(result.rows)) result.rows.forEach(pushBrand);
    if (result.row) pushBrand(result.row);
    if (result.brand && result.brand.id) pushBrand(result.brand);
  };
  (message.tool_results || []).forEach(r => walk(r.result));
  return entries;
}

function MessageBubble({ message, liveEvents }) {
  const isUser = message.role === 'user';
  const brandIndex = useMemo(() => brandIndexFromMessage(message), [message]);

  if (message.role === 'tool') {
    // Tool messages are folded into the assistant trace, never rendered
    // as their own bubble.
    return null;
  }

  if (isUser) {
    return (
      <div className="chat-bubble chat-bubble-user">
        <div className="chat-bubble-text">{message.content_md}</div>
      </div>
    );
  }

  return (
    <div className="chat-bubble chat-bubble-assistant">
      <div className="chat-bubble-markdown">
        {renderMarkdown(message.content_md, brandIndex)}
        {message.streaming && !message.content_md && (
          <span className="chat-typing">
            <span /><span /><span />
          </span>
        )}
        {message.report && (
          <ReportMessage report={message.report} />
        )}
      </div>
      {(message.tool_calls || liveEvents) && (
        <ToolCallTrace
          toolCalls={message.tool_calls || []}
          toolResults={message.tool_results || []}
          events={liveEvents || []}
        />
      )}
    </div>
  );
}

export default MessageBubble;
